import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { crewQuery, dressCodesQuery, placeLabel, rolesQuery } from '../api/crew.ts';
import {
  addToOffer,
  answerOf,
  answerOffer,
  closeOffer,
  placesLabel,
  reoffer,
  updateOffer,
  withdrawFromOffer,
  type Offer,
  type OfferRecipient,
  type Refused,
} from '../api/offers.ts';
import { localLabel } from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { CrewPicker } from '../components/crew-picker.tsx';
import { Field } from '../components/field.tsx';
import { TextArea } from '../components/text-area.tsx';
import { dollarsToMinorUnits, formatMinorUnits, minorUnitsToDollars } from '../money.ts';

// CB-REQ CRW-04 at v1.994 – v1.995, crew part 3d: an offer with places — one offer to several
// people, the first to accept taking its places. Each place taken is an ordinary shift on the job;
// the offer shows who it went to and how each answered. An edit reaches the shifts taken from it.

const BASIS = { hourly: 'per hour', daily: 'per day', flat: 'flat' } as const;
const ANSWER_TAG = {
  Taken: 'tag tag-accent',
  Waiting: 'tag',
  Declined: 'tag tag-accent-2',
  Withdrawn: 'tag',
} as const;

type Opening = 'edit' | 'add' | null;

export function OfferRow({
  offer,
  editable,
  seesPrices,
  showJob = false,
  onChanged,
}: {
  offer: Offer;
  editable: boolean;
  seesPrices: boolean;
  /** On Home and the roster, where it is not under its job. */
  showJob?: boolean;
  onChanged: () => Promise<void>;
}) {
  const [open, setOpen] = useState<Opening>(null);
  const [expanded, setExpanded] = useState(false);
  const [refused, setRefused] = useState<string[]>([]);
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: async () => {
      setOpen(null);
      await onChanged();
    },
  });
  const o = offer;
  const live = editable && o.open;

  if (open === 'edit') {
    return (
      <li>
        <OfferForm
          offer={o}
          seesPrices={seesPrices}
          onDone={() => setOpen(null)}
          onSaved={onChanged}
        />
      </li>
    );
  }

  return (
    <li className="panel flex flex-col gap-2 p-4" data-offer={o.id}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="font-semibold">
            {o.role?.name ?? (o.venue || o.location ? 'No role' : 'Held day')}
            <span className="mx-2 text-muted">·</span>
            {placesLabel(o)}
          </p>
          <p className="text-sm text-ink-soft">
            {placeLabel(o)} · {localLabel(o.window.localStart)} – {o.window.localEnd.slice(11)}
            {o.localCallAt !== o.window.localStart ? ` · call ${o.localCallAt.slice(11)}` : ''}
            {o.meetingPoint ? ` · ${o.meetingPoint}` : ''}
            {o.dressCode ? ` · ${o.dressCode.name}` : ''}
          </p>
          {showJob && o.bookedForJob ? (
            <Link to="/jobs/$jobId" params={{ jobId: o.bookedForJob.id }} className="link text-sm">
              J-{o.bookedForJob.number} {o.bookedForJob.name}
            </Link>
          ) : null}
          {seesPrices && o.rate ? (
            <p className="text-sm text-ink-soft">
              {formatMinorUnits(o.rate.amountMinor, o.rate.currency)} {BASIS[o.rate.basis]}
            </p>
          ) : null}
          {o.crewNotes ? <p className="text-sm">For crew: {o.crewNotes}</p> : null}
          {o.officeNotes ? <p className="text-sm text-ink-soft">Office: {o.officeNotes}</p> : null}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="tag" data-state="offered">
            Offered
          </span>
          {!o.open ? (
            <span className="tag">
              {o.closedAt ? 'Closed' : o.taken >= o.places ? 'Full' : 'Over'}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="quiet" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded}>
          {expanded ? 'Hide' : `Who (${o.recipients.length})`}
        </Button>
        {editable ? (
          <Button variant="quiet" onClick={() => setOpen('edit')}>
            Edit
          </Button>
        ) : null}
        {live ? (
          <Button variant="quiet" onClick={() => setOpen(open === 'add' ? null : 'add')}>
            Add people
          </Button>
        ) : null}
        {live ? (
          <Button
            variant="quiet"
            disabled={act.isPending}
            onClick={() => act.mutate(() => closeOffer(o.id))}
          >
            Close
          </Button>
        ) : null}
      </div>

      {open === 'add' ? (
        <AddPeople
          offer={o}
          onDone={async (notAdded) => {
            setRefused(notAdded);
            setOpen(null);
            setExpanded(true);
            await onChanged();
          }}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {refused.length > 0 ? (
        <div role="alert" className="alert text-sm">
          {refused.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      ) : null}
      {expanded ? (
        <ul className="flex flex-col gap-1.5 text-sm">
          {o.recipients.map((r) => (
            <Recipient
              key={r.crewMember.id}
              offer={o}
              recipient={r}
              editable={editable}
              busy={act.isPending}
              onAct={(run) => act.mutate(run)}
            />
          ))}
        </ul>
      ) : null}
      {act.error ? (
        <p role="alert" className="alert">
          {act.error.message}
        </p>
      ) : null}
    </li>
  );
}

function Recipient({
  offer,
  recipient,
  editable,
  busy,
  onAct,
}: {
  offer: Offer;
  recipient: OfferRecipient;
  editable: boolean;
  busy: boolean;
  onAct: (run: () => Promise<unknown>) => void;
}) {
  const r = recipient;
  const answer = answerOf(r);
  const who = r.crewMember.id;
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  return (
    <li className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{r.crewMember.name}</span>
        <span className={ANSWER_TAG[answer]}>{answer}</span>
        {answer === 'Declined' && r.declineReason ? (
          <span className="text-ink-soft">{r.declineReason}</span>
        ) : null}
        {editable && answer === 'Waiting' && offer.open && !declining ? (
          <>
            <Button
              variant="quiet"
              disabled={busy}
              onClick={() => onAct(() => answerOffer(offer.id, who, { answer: 'accepted' }))}
            >
              Accept<span className="sr-only"> for {r.crewMember.name}</span>
            </Button>
            <Button variant="quiet" onClick={() => setDeclining(true)}>
              Decline<span className="sr-only"> for {r.crewMember.name}</span>
            </Button>
          </>
        ) : null}
        {editable && answer === 'Waiting' && !declining ? (
          <Button
            variant="quiet"
            disabled={busy}
            onClick={() => onAct(() => withdrawFromOffer(offer.id, who))}
          >
            Withdraw<span className="sr-only"> from {r.crewMember.name}</span>
          </Button>
        ) : null}
        {editable && offer.open && (answer === 'Declined' || answer === 'Withdrawn') ? (
          <Button
            variant="quiet"
            disabled={busy}
            onClick={() => onAct(() => reoffer(offer.id, who))}
          >
            Offer again<span className="sr-only"> to {r.crewMember.name}</span>
          </Button>
        ) : null}
      </div>
      {declining ? (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Reason" value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
          <Button
            disabled={busy}
            onClick={() =>
              onAct(() =>
                answerOffer(offer.id, who, { answer: 'declined', reason: reason.trim() || null }),
              )
            }
          >
            Decline
          </Button>
          <Button variant="quiet" onClick={() => setDeclining(false)}>
            Back
          </Button>
        </div>
      ) : null}
    </li>
  );
}

export function AddPeople({
  offer,
  onDone,
  onClose,
}: {
  offer: Offer;
  /** Each person not offered it, as "Name: reason". */
  onDone: (refused: string[]) => Promise<void>;
  onClose: () => void;
}) {
  const crew = useQuery(crewQuery(false));
  const [chosen, setChosen] = useState<string[]>([]);
  const already = new Set(offer.recipients.map((r) => r.crewMember.id));
  const add = useMutation({
    mutationFn: () => addToOffer(offer.id, chosen),
    onSuccess: (made) =>
      onDone(
        made.refused.map(
          (r: Refused) =>
            `${crew.data?.find((c) => c.id === r.crewMemberId)?.displayName ?? 'Someone'}: ${r.reason}`,
        ),
      ),
  });
  return (
    <div className="flex flex-col gap-2 rounded-md bg-tile p-3">
      {/* CRW-03 at v1.9107 (crew part 4E-3): who could take a place, filtered from the offer. */}
      <CrewPicker
        work={{
          start: offer.window.localStart.slice(0, 16),
          end: offer.window.localEnd.slice(0, 16),
          venueId: offer.venue?.id,
          locationId: offer.location?.id,
          roleId: offer.role?.id,
          jobId: offer.bookedForJob?.id,
          excludeOfferId: offer.id,
        }}
        zone={offer.window.zone}
        roleName={offer.role?.name ?? null}
        exclude={[...already]}
        chosen={chosen}
        onChoose={(id) =>
          setChosen((now) => (now.includes(id) ? now.filter((x) => x !== id) : [...now, id]))
        }
      />
      {add.error ? (
        <p role="alert" className="alert">
          {add.error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button disabled={add.isPending || chosen.length === 0} onClick={() => add.mutate()}>
          Offer
        </Button>
        <Button variant="quiet" onClick={onClose}>
          Back
        </Button>
      </div>
    </div>
  );
}

/** Editing an offer: its changes reach every place taken that still matches it (CRW-04 at v1.995). */
export function OfferForm({
  offer,
  seesPrices,
  bare = false,
  formId,
  onDone,
  onSaved,
}: {
  offer: Offer;
  seesPrices: boolean;
  /** In the roster's popover: no panel, and its Save is in the popover's foot. */
  bare?: boolean;
  formId?: string;
  onDone: () => void;
  onSaved: () => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const roles = useQuery(rolesQuery);
  const dressCodes = useQuery(dressCodesQuery);
  const o = offer;
  const [roleId, setRoleId] = useState(o.role?.id ?? '');
  const [dressCodeId, setDressCodeId] = useState(o.dressCode?.id ?? '');
  const [start, setStart] = useState(o.window.localStart);
  const [end, setEnd] = useState(o.window.localEnd);
  const [callAt, setCallAt] = useState(o.localCallAt !== o.window.localStart ? o.localCallAt : '');
  const [meetingPoint, setMeetingPoint] = useState(o.meetingPoint ?? '');
  const [places, setPlaces] = useState(String(o.places));
  const [crewNotes, setCrewNotes] = useState(o.crewNotes ?? '');
  const [officeNotes, setOfficeNotes] = useState(o.officeNotes ?? '');
  const [basis, setBasis] = useState<'hourly' | 'daily' | 'flat'>(o.rate?.basis ?? 'hourly');
  const [amount, setAmount] = useState(o.rate ? minorUnitsToDollars(o.rate.amountMinor) : '');
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (changes: Parameters<typeof updateOffer>[1]) => updateOffer(o.id, changes),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
      await onSaved();
      onDone();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!start || !end || end <= start) return setProblem('Ends after it starts.');
    if (callAt && callAt > start) return setProblem('Call no later than the start.');
    const n = Number(places);
    if (!Number.isInteger(n) || n < 1) return setProblem('Places: a whole number, 1 or more.');
    let rate: { basis: typeof basis; amountMinor: string } | null | undefined;
    if (seesPrices) {
      if (amount.trim()) {
        const minor = dollarsToMinorUnits(amount);
        if (minor === null) return setProblem('Rate: dollars, e.g. 55 or 55.50.');
        rate = { basis, amountMinor: minor };
      } else rate = null;
    }
    setProblem(undefined);
    save.mutate({
      ...(roleId ? { roleId } : {}),
      window: { start, end },
      callAt: callAt || null,
      meetingPoint: meetingPoint.trim() || null,
      dressCodeId: dressCodeId || null,
      crewNotes: crewNotes.trim() || null,
      officeNotes: officeNotes.trim() || null,
      places: n,
      ...(rate === undefined ? {} : { rate }),
    });
  }

  return (
    <form
      id={formId}
      onSubmit={submit}
      className={bare ? 'flex flex-col gap-4' : 'panel flex flex-col gap-4'}
      aria-label="Offer"
    >
      {o.taken > 0 && !bare ? (
        <p className="notice text-sm">Changes the {o.taken} taken too</p>
      ) : null}
      {/* Places, role and dress code first, as the operator's OfferPopover board has them. */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Field
          label="Places"
          hint="No fewer than are taken"
          inputMode="numeric"
          value={places}
          onChange={(e) => setPlaces(e.currentTarget.value)}
        />
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`offer-role-${o.id}`} className="text-xs text-ink-soft">
            Role
          </label>
          <select
            id={`offer-role-${o.id}`}
            className="input"
            value={roleId}
            onChange={(e) => setRoleId(e.currentTarget.value)}
          >
            {o.role ? null : <option value="">Later</option>}
            {(roles.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`offer-dress-${o.id}`} className="text-xs text-ink-soft">
            Dress code
          </label>
          <select
            id={`offer-dress-${o.id}`}
            className="input"
            value={dressCodeId}
            onChange={(e) => setDressCodeId(e.currentTarget.value)}
          >
            <option value="">None yet</option>
            {(dressCodes.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field
          label="Starts"
          type="datetime-local"
          value={start}
          onChange={(e) => setStart(e.currentTarget.value)}
        />
        <Field
          label="Ends"
          type="datetime-local"
          value={end}
          onChange={(e) => setEnd(e.currentTarget.value)}
        />
        <Field
          label="Call time"
          type="datetime-local"
          value={callAt}
          onChange={(e) => setCallAt(e.currentTarget.value)}
        />
      </div>
      <Field
        label="Meeting point"
        value={meetingPoint}
        onChange={(e) => setMeetingPoint(e.currentTarget.value)}
      />
      {seesPrices ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Rate ($)"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.currentTarget.value)}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`offer-basis-${o.id}`} className="text-xs text-ink-soft">
              Per
            </label>
            <select
              id={`offer-basis-${o.id}`}
              className="input"
              value={basis}
              onChange={(e) => setBasis(e.currentTarget.value as typeof basis)}
            >
              <option value="hourly">Hour</option>
              <option value="daily">Day</option>
              <option value="flat">Flat</option>
            </select>
          </div>
        </div>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <TextArea
          label="Crew notes"
          rows={2}
          value={crewNotes}
          onChange={(e) => setCrewNotes(e.currentTarget.value)}
        />
        <TextArea
          label="Office notes"
          rows={2}
          value={officeNotes}
          onChange={(e) => setOfficeNotes(e.currentTarget.value)}
        />
      </div>
      {problem || save.isError ? (
        <p role="alert" className="alert">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      {bare ? null : (
        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>
            Save
          </Button>
          <Button type="button" variant="quiet" onClick={onDone}>
            Cancel
          </Button>
        </div>
      )}
    </form>
  );
}
